import React, { FC, useState, useEffect, useMemo } from 'react';
import { Dropdown, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { CheckinItem, useGetTrainerCheckins } from 'src/api/checkins';
import { useGetTrainerClients } from 'src/api/mutations/useGetTrainerClients';
import { useCheckinsRealtime } from 'src/hooks/useCheckinsRealtime';
import { auth } from 'src/config/firebase';
import { ReactComponent as SearchIcon } from 'src/assets/images/onboarding-trainer/dashboard/clients/search.svg';
import { ReactComponent as FilterIcon } from 'src/assets/images/onboarding-trainer/dashboard/clients/filter.svg';
import { ReactComponent as EmptyCheckinsIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/empty-checkins.svg';
import { ReactComponent as RightArrowIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/notifications/right-arrow.svg';
import './CheckinDetails.scss';

interface CheckinsListProps {
  onSelectCheckin: (checkinId: string) => void;
  searchQuery?: string;
  clientFilter?: string;
  sortFilter?: string;
  activeCheckinId?: string;
}

export const CheckinsList: FC<CheckinsListProps> = ({
  onSelectCheckin,
  searchQuery: externalSearch,
  clientFilter: externalClient,
  sortFilter: externalSort,
  activeCheckinId,
}) => {
  const isControlled = externalSearch !== undefined;

  // Search and Filter State
  const [internalSearchQuery, setInternalSearchQuery] = useState('');
  const [internalClientFilter, setInternalClientFilter] = useState('all');
  const [internalSortFilter, setInternalSortFilter] = useState('newest'); // 'newest', 'oldest'

  const activeSearch = isControlled ? externalSearch : internalSearchQuery;
  const activeClient = isControlled ? externalClient || 'all' : internalClientFilter;
  const activeSort = isControlled ? externalSort || 'newest' : internalSortFilter;

  const [debouncedSearch, setDebouncedSearch] = useState(activeSearch);

  const trainerUid = auth.currentUser?.uid || '';

  // 1. Fetch server checkins from Cloud Functions backend via React Query
  const { data: serverCheckinsData, isLoading: isServerLoading } = useGetTrainerCheckins(
    {
      search: debouncedSearch || undefined,
      clientId: activeClient !== 'all' && activeClient !== 'All clients' ? activeClient : undefined,
      sort: activeSort,
    },
    Boolean(trainerUid),
  );

  // 2. Realtime checkins from flutter_chat_messages/{chatId}/checkins
  const { checkins: realtimeCheckins, isLoading: isRealtimeLoading } =
    useCheckinsRealtime(trainerUid);

  // Track check-ins marked as read locally for immediate optimistic UI update
  const [readCheckinIds, setReadCheckinIds] = useState<Set<string>>(new Set());

  // Clients Dropdown Options
  const { data: clientsData } = useGetTrainerClients('all', '', 'name_asc', 1, 1000);
  const clientOptions = useMemo(() => {
    const list = clientsData?.clients || [];
    return [
      { key: 'all', label: 'All clients' },
      ...list.map((c) => ({ key: c.uid, label: `${c.name} ${c.surname}` })),
    ];
  }, [clientsData]);

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(activeSearch);
    }, 400);
    return () => clearTimeout(handler);
  }, [activeSearch]);

  const getCheckinTimestamp = (item: CheckinItem): number => {
    const val = item.status_updated_at || item.updated_at || item.created_at;
    if (!val) {
      const fallback = item.datetime_utc || item.date || '';
      return new Date(fallback).getTime() || 0;
    }
    if (typeof (val as any).toDate === 'function') {
      return (val as any).toDate().getTime();
    }
    if (typeof val === 'number') return val;
    if (typeof val === 'string') {
      const parsed = new Date(val).getTime();
      return isNaN(parsed) ? 0 : parsed;
    }
    if ((val as any).seconds) return (val as any).seconds * 1000;
    return 0;
  };

  // Derived filtered & sorted check-ins combining backend data + realtime updates
  const displayCheckins = useMemo(() => {
    const map = new Map<string, CheckinItem>();

    // Seed with server data from Cloud Functions backend
    (serverCheckinsData?.items || []).forEach((c) => {
      const key = c.message_id || c.id;
      map.set(key, c);
    });

    // Merge realtime updates
    realtimeCheckins.forEach((c) => {
      if (!c.isAppoint && !c.appointUId) return;
      if (activeClient && activeClient !== 'all' && activeClient !== 'All clients') {
        if (c.client_uid !== activeClient) return;
      }
      if (debouncedSearch) {
        const q = debouncedSearch.toLowerCase();
        const matches =
          (c.client_name || '').toLowerCase().includes(q) ||
          (c.notes || '').toLowerCase().includes(q) ||
          (c.preference || '').toLowerCase().includes(q);
        if (!matches) return;
      }
      const key = c.message_id || c.id;
      const isLocallyRead = readCheckinIds.has(key) || readCheckinIds.has(c.id);
      map.set(key, {
        ...map.get(key),
        ...c,
        id: key,
        message_id: c.message_id || key,
        is_read: isLocallyRead ? true : c.is_read,
      });
    });

    const list = Array.from(map.values());

    // Sort by created / updated date
    list.sort((a, b) => {
      const timeA = getCheckinTimestamp(a);
      const timeB = getCheckinTimestamp(b);
      return activeSort === 'oldest' ? timeA - timeB : timeB - timeA;
    });

    return list;
  }, [
    serverCheckinsData,
    realtimeCheckins,
    activeClient,
    debouncedSearch,
    activeSort,
    readCheckinIds,
  ]);

  const isLoading =
    (isServerLoading || isRealtimeLoading || serverCheckinsData === undefined) &&
    displayCheckins.length === 0;

  const getClientLabel = () => {
    if (activeClient === 'all') return 'All clients';
    const found = clientOptions.find((c) => c.key === activeClient);
    return found ? found.label : 'All clients';
  };

  const sortItems = [
    { key: 'newest', label: 'Date (Newest)' },
    { key: 'oldest', label: 'Date (Oldest)' },
  ];

  const getSortLabel = () => {
    const found = sortItems.find((s) => s.key === activeSort);
    return found ? found.label : 'Date (Newest)';
  };

  const getInitials = (name?: string) => {
    if (!name || name === 'Client') return 'GU';
    const trimmed = name.trim();
    if (!trimmed) return 'GU';
    const parts = trimmed.split(/\s+/);
    if (parts.length > 1) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return trimmed.substring(0, 2).toUpperCase();
  };

  const formatCheckinSentTime = (item: CheckinItem) => {
    const timestamp = getCheckinTimestamp(item);
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const isToday =
      date.getDate() === now.getDate() &&
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear();

    const yesterday = new Date(now.getTime() - 86400000);
    const isYesterday =
      date.getDate() === yesterday.getDate() &&
      date.getMonth() === yesterday.getMonth() &&
      date.getFullYear() === yesterday.getFullYear();

    const timeStr = date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    if (isToday) return `Sent at ${timeStr}`;
    if (isYesterday) return 'Yesterday';
    const dateStr = date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    return dateStr;
  };

  const formatSubtitle = (checkin: CheckinItem) => {
    let dateStr = checkin.date || 'Jul 23, 2026';
    let timeStr = checkin.time || '12:00';
    try {
      const utcString = checkin.suggested_datetime_utc || checkin.datetime_utc;
      if (utcString) {
        const d = new Date(utcString);
        dateStr = d.toLocaleDateString('en-US', {
          month: 'short',
          day: '2-digit',
          year: 'numeric',
        });
        timeStr = d.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });
      } else if (checkin.date) {
        const d = new Date(checkin.date);
        dateStr = d.toLocaleDateString('en-US', {
          month: 'short',
          day: '2-digit',
          year: 'numeric',
        });
      }
    } catch {
      // fallback
    }

    let prefStr = 'Online via Zoom';
    if (checkin.preference === 'in_person') prefStr = 'In-person';
    if (checkin.preference === 'phone') prefStr = 'Phone call';

    return `${dateStr} · ${timeStr} · ${prefStr}`;
  };

  const renderBadge = (item: CheckinItem) => {
    if (item.status_badge) {
      return (
        <div className={`status-badge ${item.status_badge.className}`}>
          {item.status_badge.hasDot && <span className="status-dot" />}
          <span>{item.status_badge.label}</span>
        </div>
      );
    }
    const st = item.status || 'pending_confirmation';
    return (
      <div className={`status-badge ${st}`}>
        <span>{st}</span>
      </div>
    );
  };

  // When used inside DashboardMessages (isControlled)
  if (isControlled) {
    if (isLoading) {
      return (
        <div
          className="loading-container"
          style={{
            padding: '40px 0',
            minHeight: 'auto',
            textAlign: 'center',
            width: '100%',
            color: '#A0A0A0',
          }}
        >
          <Spin indicator={<LoadingOutlined style={{ fontSize: 28, color: '#F59E0B' }} spin />} />
          <div style={{ marginTop: '12px', fontSize: '14px', color: '#8E95A5' }}>
            Loading check-ins...
          </div>
        </div>
      );
    }

    if (displayCheckins.length === 0) {
      return (
        <div className="dashboard-empty-state">
          <div className="empty-icon">
            <EmptyCheckinsIcon />
          </div>
          <h2>
            {debouncedSearch ? `No check-ins found for "${debouncedSearch}"` : 'No check-ins found'}
          </h2>
          <p>
            {debouncedSearch
              ? 'Try a different search term or client filter.'
              : 'You have no check-in requests at the moment.'}
          </p>
        </div>
      );
    }

    return (
      <div className="checkins-list-container">
        {displayCheckins.map((item) => {
          const targetId = item.message_id || item.id;
          const isSelected =
            Boolean(activeCheckinId) &&
            (targetId === activeCheckinId || item.id === activeCheckinId);

          return (
            <div
              key={targetId}
              className={`checkin-card ${isSelected ? 'active' : ''}`}
              onClick={() => {
                setReadCheckinIds((prev) => new Set(prev).add(targetId));
                onSelectCheckin(targetId);
              }}
              role="button"
              tabIndex={0}
            >
              <div className="card-top">
                <div
                  className="badge-container"
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  {renderBadge(item)}
                  {!item.is_read &&
                    !readCheckinIds.has(targetId) &&
                    !readCheckinIds.has(item.id) && (
                      <div className="unread-indicator">
                        <div className="unread-dot" style={{ backgroundColor: '#F59E0B' }}></div>
                        <span style={{ color: '#F59E0B' }}>NEW</span>
                      </div>
                    )}
                </div>
              </div>

              <div className="card-main">
                <div className="user-info">
                  <div
                    className={`avatar ${item.client_avatar ? 'has-image' : ''}`}
                    style={
                      item.client_avatar
                        ? { backgroundImage: `url(${item.client_avatar})` }
                        : undefined
                    }
                  >
                    {!item.client_avatar &&
                      (item.client_initials && item.client_initials !== 'CL'
                        ? item.client_initials
                        : getInitials(item.client_name))}
                  </div>
                  <div className="name-and-time">
                    <h3>
                      {item.client_name && item.client_name !== 'Client'
                        ? item.client_name
                        : 'Guest'}
                    </h3>
                    <span className="sent-time">
                      {item.formatted_sent_time || formatCheckinSentTime(item)}
                    </span>
                  </div>
                </div>

                <RightArrowIcon className="chevron-icon" />
              </div>

              <div className="card-divider" />

              <div className="card-bottom">
                <p className="subtitle">{item.formatted_subtitle || formatSubtitle(item)}</p>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div
      className="checkins-list-wrapper"
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
      }}
    >
      {/* Top Filter Bar (only rendered when CheckinsList is used standalone without external filters) */}
      <div className="dashboard-filter-bar" style={{ marginTop: '16px' }}>
        <div className="filters-left">
          <div className="search-input">
            <SearchIcon />
            <input
              type="text"
              placeholder="Search check-ins"
              value={internalSearchQuery}
              onChange={(e) => setInternalSearchQuery(e.target.value)}
              onBlur={(e) => setInternalSearchQuery(e.target.value.trim())}
            />
            {internalSearchQuery && (
              <button
                type="button"
                onClick={() => setInternalSearchQuery('')}
                className="clear-search-btn"
                aria-label="Clear search"
                style={{
                  background: 'transparent',
                  backgroundColor: 'transparent',
                  backgroundImage: 'none',
                  border: 'none',
                  padding: 0,
                  margin: 0,
                  outline: 'none',
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  borderRadius: 0,
                  boxShadow: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: '#838e9e',
                  width: 20,
                  height: 20,
                  minWidth: 'unset',
                  minHeight: 'unset',
                  flexShrink: 0,
                }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
          </div>
        </div>

        <div className="filters-right">
          <div className="filter-label">
            <FilterIcon />
            <span>Filters:</span>
          </div>

          <Dropdown
            menu={{ items: clientOptions, onClick: (e) => setInternalClientFilter(e.key) }}
            trigger={['click']}
            rootClassName="dark-theme-dropdown"
          >
            <div className="filter-item filter-client" style={{ cursor: 'pointer' }}>
              <span>
                Client: <span className="value">{getClientLabel()}</span>
              </span>
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </div>
          </Dropdown>

          <Dropdown
            menu={{ items: sortItems, onClick: (e) => setInternalSortFilter(e.key) }}
            trigger={['click']}
            rootClassName="dark-theme-dropdown"
          >
            <div className="filter-item filter-sort" style={{ cursor: 'pointer' }}>
              <span>
                Sort: <span className="value">{getSortLabel()}</span>
              </span>
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </div>
          </Dropdown>
        </div>
      </div>

      {/* Checkins List Content */}
      {isLoading ? (
        <div
          className="loading-container"
          style={{
            padding: '60px 0',
            textAlign: 'center',
            width: '100%',
            color: '#A0A0A0',
          }}
        >
          <Spin indicator={<LoadingOutlined style={{ fontSize: 28, color: '#F59E0B' }} spin />} />
          <div style={{ marginTop: '12px', fontSize: '14px', color: '#8E95A5' }}>
            Loading check-ins...
          </div>
        </div>
      ) : displayCheckins.length > 0 ? (
        <div className="checkins-list-container">
          {displayCheckins.map((item) => {
            const targetId = item.message_id || item.id;
            const isSelected =
              Boolean(activeCheckinId) &&
              (targetId === activeCheckinId || item.id === activeCheckinId);

            return (
              <div
                key={targetId}
                className={`checkin-card ${isSelected ? 'active' : ''}`}
                onClick={() => {
                  setReadCheckinIds((prev) => new Set(prev).add(targetId));
                  onSelectCheckin(targetId);
                }}
                role="button"
                tabIndex={0}
              >
                <div className="card-top">
                  <div
                    className="badge-container"
                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    {renderBadge(item)}
                    {!item.is_read &&
                      !readCheckinIds.has(targetId) &&
                      !readCheckinIds.has(item.id) && (
                        <div className="unread-indicator">
                          <div className="unread-dot" style={{ backgroundColor: '#F59E0B' }}></div>
                          <span style={{ color: '#F59E0B' }}>NEW</span>
                        </div>
                      )}
                  </div>
                </div>

                <div className="card-main">
                  <div className="user-info">
                    <div
                      className={`avatar ${item.client_avatar ? 'has-image' : ''}`}
                      style={
                        item.client_avatar
                          ? { backgroundImage: `url(${item.client_avatar})` }
                          : undefined
                      }
                    >
                      {!item.client_avatar &&
                        (item.client_initials && item.client_initials !== 'CL'
                          ? item.client_initials
                          : getInitials(item.client_name))}
                    </div>
                    <div className="name-and-time">
                      <h3>
                        {item.client_name && item.client_name !== 'Client'
                          ? item.client_name
                          : 'Guest'}
                      </h3>
                      <span className="sent-time">
                        {item.formatted_sent_time || formatCheckinSentTime(item)}
                      </span>
                    </div>
                  </div>

                  <RightArrowIcon className="chevron-icon" />
                </div>

                <div className="card-divider" />

                <div className="card-bottom">
                  <p className="subtitle">{item.formatted_subtitle || formatSubtitle(item)}</p>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="dashboard-empty-state">
          <div className="empty-icon">
            <EmptyCheckinsIcon />
          </div>
          <h2>
            {debouncedSearch ? `No check-ins found for "${debouncedSearch}"` : 'No check-ins found'}
          </h2>
          <p>
            {debouncedSearch
              ? 'Try a different search term or client filter.'
              : 'You have no check-in requests at the moment.'}
          </p>
        </div>
      )}
    </div>
  );
};
